// src/products/analysis/pages/TreasuryBondsPage.js
import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, TrendingUp, TrendingDown, RefreshCw, Info } from 'lucide-react';
import { getAllTreasuryYields, getTreasuryHistory, TREASURY_SERIES } from '../services/treasuryService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const TreasuryBondsPage = () => {
  const [treasuries, setTreasuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTreasury, setSelectedTreasury] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [timePeriod, setTimePeriod] = useState('90');

  const timePeriods = [
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '180', label: '180 Days' },
    { value: '365', label: '1 Year' },
    { value: '730', label: '2 Years' }
  ];

  useEffect(() => {
    loadTreasuryData();
  }, []);

  useEffect(() => {
    if (selectedTreasury) {
      loadChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreasury, timePeriod]);

  const loadTreasuryData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllTreasuryYields();
      
      // Initialize with all treasuries
      const allTreasuries = Object.values(TREASURY_SERIES).map(t => {
        const fetched = data.find(d => d.id === t.id);
        return fetched || {
          ...t,
          currentYield: null,
          previousYield: null,
          yieldChange: null,
          yieldChangePercent: null,
          date: null,
          error: 'Not loaded'
        };
      });

      setTreasuries(allTreasuries);
    } catch (err) {
      let errorMessage = err.message || 'Failed to load Treasury data. Please try again.';
      
      if (errorMessage.includes('API key')) {
        errorMessage = 'FRED API key not configured. Please add your FRED API key to treasuryService.js. Get a free key at https://fred.stlouisfed.org/docs/api/api_key.html';
      }
      
      setError(errorMessage);
      console.error('Treasury loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    if (!selectedTreasury) return;

    setLoadingChart(true);
    try {
      const history = await getTreasuryHistory(selectedTreasury.id, parseInt(timePeriod));
      
      const formattedData = history.map(d => ({
        date: new Date(d.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        timestamp: d.timestamp,
        yield: d.yield,
        close: d.yield // For chart compatibility
      }));

      setChartData(formattedData);
    } catch (err) {
      console.error('Chart loading error:', err);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  const formatYield = (yieldValue) => {
    if (yieldValue === null || yieldValue === undefined) return 'N/A';
    return `${yieldValue.toFixed(2)}%`;
  };

  const formatChange = (change, changePercent) => {
    if (changePercent === null || changePercent === undefined) {
      if (change === null || change === undefined) return 'N/A';
      return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
    }
    const isPositive = changePercent >= 0;
    const sign = isPositive ? '+' : '';
    return `${sign}${changePercent.toFixed(2)}%`;
  };

  // Group by category (Bills, Notes, Bonds)
  const bills = treasuries.filter(t => ['1MO', '3MO', '6MO'].includes(t.id));
  const notes = treasuries.filter(t => ['1Y', '2Y', '3Y', '5Y', '7Y', '10Y'].includes(t.id));
  const bonds = treasuries.filter(t => ['20Y', '30Y'].includes(t.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treasury Yields</h1>
          <p className="mt-1 text-sm text-gray-500">
            U.S. Treasury Bill, Note, and Bond yields from FRED
          </p>
        </div>
        <button
          onClick={loadTreasuryData}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh
        </button>
      </div>

      {/* API Key Warning */}
      {error && error.includes('API key') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
            <p className="text-xs mt-2">
              1. Visit <a href="https://fred.stlouisfed.org/docs/api/api_key.html" target="_blank" rel="noopener noreferrer" className="underline">FRED API Key page</a> and register for a free account
            </p>
            <p className="text-xs mt-1">
              2. Get your API key and update <code className="bg-yellow-100 px-1 rounded">FRED_API_KEY</code> in <code className="bg-yellow-100 px-1 rounded">src/products/analysis/services/treasuryService.js</code>
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && !error.includes('API key') && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading Treasury data...</span>
        </div>
      )}

      {/* Treasury Cards */}
      {!loading && treasuries.length > 0 && (
        <div className="space-y-8">
          {/* Treasury Bills */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Treasury Bills</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bills.map((treasury) => {
                const isPositive = treasury.yieldChangePercent !== null && treasury.yieldChangePercent >= 0;
                const hasError = treasury.error && treasury.error !== 'Not loaded';

                return (
                  <div
                    key={treasury.id}
                    onClick={() => !hasError && setSelectedTreasury(treasury)}
                    className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${!hasError ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{treasury.name}</h3>
                        <p className="text-sm text-gray-500">{treasury.maturity}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Current Yield */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Current Yield</p>
                        {hasError ? (
                          <p className="text-sm text-red-500">{treasury.error}</p>
                        ) : treasury.currentYield !== null ? (
                          <p className="text-2xl font-bold text-gray-900">
                            {formatYield(treasury.currentYield)}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">Loading...</p>
                        )}
                      </div>

                      {/* Yield Change */}
                      {!hasError && treasury.yieldChangePercent !== null && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Change</p>
                          <div className="flex items-center gap-2">
                            {isPositive ? (
                              <TrendingUp className="w-5 h-5 text-green-500" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-500" />
                            )}
                            <p
                              className={`text-lg font-semibold ${
                                isPositive ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {formatChange(treasury.yieldChange, treasury.yieldChangePercent)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Last Updated */}
                      {treasury.date && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Updated: {new Date(treasury.date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Treasury Notes */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Treasury Notes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {notes.map((treasury) => {
                const isPositive = treasury.yieldChangePercent !== null && treasury.yieldChangePercent >= 0;
                const hasError = treasury.error && treasury.error !== 'Not loaded';

                return (
                  <div
                    key={treasury.id}
                    onClick={() => !hasError && setSelectedTreasury(treasury)}
                    className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${!hasError ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{treasury.name}</h3>
                        <p className="text-sm text-gray-500">{treasury.maturity}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Current Yield</p>
                        {hasError ? (
                          <p className="text-sm text-red-500">{treasury.error}</p>
                        ) : treasury.currentYield !== null ? (
                          <p className="text-2xl font-bold text-gray-900">
                            {formatYield(treasury.currentYield)}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">Loading...</p>
                        )}
                      </div>

                      {!hasError && treasury.yieldChangePercent !== null && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Change</p>
                          <div className="flex items-center gap-2">
                            {isPositive ? (
                              <TrendingUp className="w-5 h-5 text-green-500" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-500" />
                            )}
                            <p
                              className={`text-lg font-semibold ${
                                isPositive ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {formatChange(treasury.yieldChange, treasury.yieldChangePercent)}
                            </p>
                          </div>
                        </div>
                      )}

                      {treasury.date && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Updated: {new Date(treasury.date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Treasury Bonds */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Treasury Bonds</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bonds.map((treasury) => {
                const isPositive = treasury.yieldChangePercent !== null && treasury.yieldChangePercent >= 0;
                const hasError = treasury.error && treasury.error !== 'Not loaded';

                return (
                  <div
                    key={treasury.id}
                    onClick={() => !hasError && setSelectedTreasury(treasury)}
                    className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${!hasError ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{treasury.name}</h3>
                        <p className="text-sm text-gray-500">{treasury.maturity}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Current Yield</p>
                        {hasError ? (
                          <p className="text-sm text-red-500">{treasury.error}</p>
                        ) : treasury.currentYield !== null ? (
                          <p className="text-2xl font-bold text-gray-900">
                            {formatYield(treasury.currentYield)}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">Loading...</p>
                        )}
                      </div>

                      {!hasError && treasury.yieldChangePercent !== null && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Change</p>
                          <div className="flex items-center gap-2">
                            {isPositive ? (
                              <TrendingUp className="w-5 h-5 text-green-500" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-500" />
                            )}
                            <p
                              className={`text-lg font-semibold ${
                                isPositive ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {formatChange(treasury.yieldChange, treasury.yieldChangePercent)}
                            </p>
                          </div>
                        </div>
                      )}

                      {treasury.date && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Updated: {new Date(treasury.date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Chart Section */}
      {selectedTreasury && !selectedTreasury.error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {selectedTreasury.name} Yield History
              </h2>
              <p className="text-sm text-gray-500 mt-1">Historical yield data</p>
            </div>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {timePeriods.map(period => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          {loadingChart ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading chart data...</span>
            </div>
          ) : chartData.length > 0 ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80} 
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tickFormatter={(value) => `${value.toFixed(2)}%`} 
                    domain={['dataMin', 'dataMax']} 
                    width={60}
                  />
                  <Tooltip 
                    formatter={(value) => [`${parseFloat(value).toFixed(2)}%`, 'Yield']} 
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: '6px' 
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="yield" 
                    stroke="#2563eb" 
                    strokeWidth={2} 
                    dot={false} 
                    activeDot={{ r: 4 }}
                    name="Yield (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No chart data available</p>
            </div>
          )}
        </div>
      )}

      {/* Info Message */}
      {!loading && treasuries.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2 text-blue-800">
          <Info className="w-5 h-5 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">Treasury Yield Data</p>
            <p className="text-xs mt-1">
              Data from FRED (Federal Reserve Economic Data). Click on any Treasury to view historical yield chart. 
              Yields are annualized percentages.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasuryBondsPage;

